/**
 * SignaturePad — Composant canvas réutilisable pour capturer une signature manuscrite.
 *
 * Retourne un data URL PNG via la ref exposée (`getDataURL()`) ou via la prop `onChange`.
 * Supporte mouse + touch. Ligne noire, épaisseur 2.5px, canvas plein en blanc.
 */
import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Eraser } from 'lucide-react';

const SignaturePad = forwardRef(({ width, height = 160, onChange, disabled = false }, ref) => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [drawing, setDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [w, setW] = useState(width || 320);
  const lastPos = useRef(null);

  // Keep onChange ref up to date sans déclencher les effets
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const getCtx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    return c.getContext('2d');
  };

  // Auto-fit width au parent si width n'est pas donné
  useEffect(() => {
    if (width) { setW(width); return; }
    const measure = () => {
      const parent = wrapperRef.current?.parentElement;
      if (parent) {
        const rect = parent.getBoundingClientRect();
        setW(Math.max(240, Math.floor(rect.width)));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapperRef.current?.parentElement) ro.observe(wrapperRef.current.parentElement);
    return () => ro.disconnect();
  }, [width]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Retina : double la résolution interne
    const dpr = window.devicePixelRatio || 1;
    // Sauvegarder l'image actuelle avant redimensionnement (si non vide)
    let previousImage = null;
    if (!isEmpty && c.width > 0 && c.height > 0) {
      try {
        previousImage = c.toDataURL('image/png');
      } catch (_) { /* noop */ }
    }
    c.width = w * dpr;
    c.height = height * dpr;
    c.style.width = `${w}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, height);
    ctx.strokeStyle = '#0d1a12';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (previousImage) {
      // Restaurer la signature après resize (préserve les traits déjà dessinés)
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0, w, height); };
      img.src = previousImage;
      // Ne pas reset isEmpty ni onChange
    } else {
      setIsEmpty(true);
      if (onChangeRef.current) onChangeRef.current('');
    }
  }, [w, height]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e) => {
    if (disabled) return;
    e.preventDefault();
    setDrawing(true);
    lastPos.current = getPos(e);
  };

  const draw = (e) => {
    if (!drawing || disabled) return;
    e.preventDefault();
    const ctx = getCtx();
    const pos = getPos(e);
    if (ctx && lastPos.current) {
      ctx.beginPath();
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    lastPos.current = pos;
    if (isEmpty) setIsEmpty(false);
  };

  const endDraw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    setDrawing(false);
    lastPos.current = null;
    if (onChangeRef.current && canvasRef.current) {
      onChangeRef.current(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, height);
    setIsEmpty(true);
    if (onChangeRef.current) onChangeRef.current('');
  };

  useImperativeHandle(ref, () => ({
    getDataURL: () => (isEmpty ? '' : canvasRef.current?.toDataURL('image/png')),
    isEmpty: () => isEmpty,
    clear,
  }));

  return (
    <div className="relative select-none w-full" ref={wrapperRef} data-testid="signature-pad-wrapper">
      <canvas
        ref={canvasRef}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
        style={{ touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair' }}
        className="block rounded-md border-2 border-dashed border-[#d4a850] bg-white"
        data-testid="signature-pad-canvas"
      />
      {isEmpty && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[#8a7a56] italic text-sm">
          Signez ici avec votre souris ou votre doigt
        </div>
      )}
      <button
        type="button"
        onClick={clear}
        disabled={disabled || isEmpty}
        className="absolute top-2 right-2 flex items-center gap-1 rounded bg-white/95 px-2 py-1 text-xs text-neutral-600 hover:text-red-600 hover:bg-white shadow-sm border border-neutral-200 disabled:opacity-30"
        data-testid="signature-pad-clear-btn"
      >
        <Eraser size={12} />
        Effacer
      </button>
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';
export default SignaturePad;
